import type {
  AdapterDefinition,
  AdapterMeta,
  GlobalCrawlConfigInput,
  SuburbInput,
} from "@repo/adapter-core";
import { defineAdapter, GlobalCrawlConfigSchema } from "@repo/adapter-core";
import { z } from "zod";
import { parseGenericListingHtml } from "./generic-parser.ts";

export const soldOnlyAdapterConfigSchema = GlobalCrawlConfigSchema.extend({
  listingType: z.literal("sold").default("sold"),
});

export type SoldOnlyAdapterConfig = z.infer<typeof soldOnlyAdapterConfigSchema>;

export function resolveSoldOnlyAdapterConfig(
  config: GlobalCrawlConfigInput = {},
): SoldOnlyAdapterConfig {
  return soldOnlyAdapterConfigSchema.parse({
    ...config,
    listingType: "sold",
  });
}

export interface SoldOnlyAdapterOptions {
  parseDetailHtml?: (html: string, url: string) => Record<string, unknown>;
  /** Send search pages to Bright Data first. See AdapterDefinition.brightdataDiscoveryFirst. */
  brightdataDiscoveryFirst?: boolean;
  /**
   * Brightdata Scraping Browser env-var name — set for SPA-hydration
   * adapters (McGrath catch-all, property.com.au) so Playwright drives
   * a remote Chromium when Web Unlocker's `format=raw` returns a shell.
   */
  brightdataBrowserZoneEnvVar?: string;
  /** Direct JSON-API discovery — see AdapterDefinition.directApiDiscovery. */
  directApiDiscovery?: AdapterDefinition<SoldOnlyAdapterConfig>["directApiDiscovery"];
  /** Try a free direct fetch before any paid provider. See AdapterDefinition.directFirst. */
  directFirst?: boolean;
  /** See AdapterDefinition.directTimeoutMs. */
  directTimeoutMs?: number;
  /** Page N of a search; the registry's `?page=N` default otherwise. */
  buildPaginatedUrl?: AdapterDefinition<SoldOnlyAdapterConfig>["buildPaginatedUrl"];
  /** Discovery cards (and their URLs) from a search page. See AdapterDefinition.parseDiscoveryPreviews. */
  parseDiscoveryPreviews?: AdapterDefinition<SoldOnlyAdapterConfig>["parseDiscoveryPreviews"];
  /** See AdapterDefinition.discoveryCacheKey. */
  discoveryCacheKey?: AdapterDefinition<SoldOnlyAdapterConfig>["discoveryCacheKey"];
}

export function defineSoldOnlyAdapter(
  meta: AdapterMeta,
  buildSearchUrl: (suburb: SuburbInput, config: SoldOnlyAdapterConfig) => string,
  options: SoldOnlyAdapterOptions = {},
): AdapterDefinition<SoldOnlyAdapterConfig> {
  // Adapters without a site-specific overlay parser default to the
  // generic JSON-LD + microdata parser. The overlay contract guarantees
  // this never overrides fields Firecrawl already populated — it only
  // fills missing ones, so it's safe to enable broadly. Per-adapter
  // parsers (kay-burton-bayside/parser.ts, marshall-white/parser.ts,
  // realestate-com-au/parser.ts) take priority when set.
  return defineAdapter({
    meta,
    defaultConfig: { listingType: "sold" },
    resolvedConfigSchema: soldOnlyAdapterConfigSchema,
    resolveConfig: resolveSoldOnlyAdapterConfig,
    buildSearchUrl,
    parseDetailHtml: options.parseDetailHtml ?? parseGenericListingHtml,
    brightdataDiscoveryFirst: options.brightdataDiscoveryFirst,
    brightdataBrowserZoneEnvVar: options.brightdataBrowserZoneEnvVar,
    directApiDiscovery: options.directApiDiscovery,
    directFirst: options.directFirst,
    directTimeoutMs: options.directTimeoutMs,
    buildPaginatedUrl: options.buildPaginatedUrl,
    parseDiscoveryPreviews: options.parseDiscoveryPreviews,
    discoveryCacheKey: options.discoveryCacheKey,
  });
}
